import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { useMemo } from "react";
import { useCustomerJson } from "./customHooks/genericHooks";
import { useCustomerKey } from "./customHooks/useCustomerKey";
import { isDstObserved, ONE_HOUR_OF_MS, ONE_MINUTE_OF_MS } from "./timeUtils";
import { timeZonesWikiData } from "./timeZonesWikiData";
import { useIsStorybook } from "./customHooks/storyHooks";
import { useCustomerInfoJsonAtom } from "reports/utils/hooks/useCustomerInfoJson";

/** When mocking timeZones (via Settings page),
 * the hours in datetime-picker display dates formatted in mocked timezones.
 * To compensate, we subtract (system timeZone + mocked timeZone).
 *
 * (used in TrendsPageDateRangeStartAndEnd)
 */
export function useTimeZoneMsOffset(): number {
  const { shouldUseCustomTimeZone, timeZoneLocaleString } = useSettings();

  const offsetMsFromSettingsPage =
    getTimeZoneOffsetMsFromLocaleString(timeZoneLocaleString)
      .timezoneOffsetMs || 0;

  const offsetMsFromSystem = new Date().getTimezoneOffset() * ONE_MINUTE_OF_MS;

  return useMemo(
    () =>
      shouldUseCustomTimeZone
        ? -offsetMsFromSystem - offsetMsFromSettingsPage
        : 0,
    [shouldUseCustomTimeZone, offsetMsFromSystem, offsetMsFromSettingsPage]
  );
}

/** the difference (in ms) between our current timezone and the customer's timezone
 *
 * (used in TrendChartBarsGetter)
 */
export function useCustomerTimeZoneOffsetMs() {
  const isStorybook = useIsStorybook();
  const ckey = useCustomerKey();
  const currentTimezoneOffsetMinutes = new Date().getTimezoneOffset();
  const currentTimezoneOffsetMs =
    currentTimezoneOffsetMinutes * ONE_MINUTE_OF_MS;
  const { timezoneOffsetMs } = useCustomerTimeZone(ckey);
  return isStorybook
    ? 0
    : timezoneOffsetMs && timezoneOffsetMs + currentTimezoneOffsetMs;
}

/** readable timezone for the customer e.g. "US/Eastern (UTC -05:00)" */
export function useCustomerTimeZone(ckey: string) {
  const customerJsonFromAtom = useCustomerInfoJsonAtom();
  const customerJsonStored = useCustomerJson();
  const customerJson = customerJsonFromAtom || customerJsonStored;

  const { timezoneOffsetMs, tzUtcOffset } = useMemo(
    () => getTimeZoneOffsetMsFromLocaleString(customerJson?.timeZone),
    [customerJson?.timeZone]
  );

  return {
    timezoneOffsetMs,
    timezoneReadable: customerJson?.timeZone
      ? `${customerJson?.timeZone} (UTC ${tzUtcOffset})`
      : "",
  };
}

export function getTimeZoneOffsetMsFromLocaleString(timeZone?: string) {
  const tzUtcOffset =
    timeZone &&
    timeZonesWikiData.find(row => row["TZ database name"] === timeZone)?.[
      isDstObserved() ? "UTC DST offset ±hh:mm" : "UTC offset ±hh:mm"
    ];

  // convert e.g. "+02:00" to 2 * ONE_HOUR_OF_MS
  const isPositive = tzUtcOffset?.substring(0, 1) === "+";
  const [hours, minutes] = tzUtcOffset
    ? tzUtcOffset
        .substring(1)
        .split(":")
        .map(numString => parseInt(numString, 10))
    : [null, null];

  const timezoneOffsetMs =
    (hours || hours === 0) && (minutes || minutes === 0)
      ? (hours * ONE_HOUR_OF_MS + minutes * ONE_MINUTE_OF_MS) *
        (isPositive ? 1 : -1)
      : null;
  return { timezoneOffsetMs, tzUtcOffset };
}
